// ════════════════════════════════════════════════════════════════════════════
//  AvisosDelAlumno.jsx
//  «AVÍSAME EN MI TELÉFONO», EN EL CENTRO DE ALERTAS
//
//  Rob, 30-09-2026: «también un botón de desactivar notificaciones en dashboard
//  alertas, por si el usuario decide no recibirlas».
//
//  Va aquí y no en Configuración por el mismo motivo que el de gerencia está en
//  su bandeja: ésta es la pantalla donde el alumno lee lo que le pasa, y esto
//  decide si eso le llega al teléfono o espera a que entre. Un interruptor
//  escondido en otro menú es un interruptor que nadie encuentra — ni para
//  encenderlo ni, lo que importa más, para apagarlo.
//
//  ── LO QUE ESTA PANTALLA TIENE QUE DECIR LA VERDAD ─────────────────────────
//  Hay dos cosas distintas y la pantalla no puede confundirlas:
//
//    · El APARATO. «Quiero avisos en este teléfono.» Es de este aparato y de
//      ningún otro, y apagarlo BORRA el aparato — se lo promete el aviso de
//      privacidad.
//    · Los GRUPOS. «De lo que me llegue, quiero esto y no aquello.» Es de la
//      persona y vale para todos sus aparatos.
//
//  Y el caso que más se presta a mentir: interruptores encendidos y ningún
//  aparato. Eso no manda nada, y una pantalla que sólo enseñara los grupos
//  estaría diciendo que sí a algo que no pasa. Por eso el aparato va primero y
//  los grupos sólo se explican cuando hay dónde recibirlos.
// ════════════════════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useState } from "react";
import apiClient from "../../../api/apiClient";
import "./AvisosDelAlumno.css";

const SOPORTADO = typeof window !== "undefined"
  && "serviceWorker" in navigator
  && "PushManager" in window
  && "Notification" in window;

/** La llave pública viaja en base64 «de url»; el navegador la quiere en bytes. */
function aBytes(base64url) {
  const relleno = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + relleno).replace(/-/g, "+").replace(/_/g, "/");
  const crudo = window.atob(base64);
  const bytes = new Uint8Array(crudo.length);
  for (let i = 0; i < crudo.length; i += 1) bytes[i] = crudo.charCodeAt(i);
  return bytes;
}

function nombreDelAparato() {
  const ua = navigator.userAgent || "";
  const sistema = /Android/i.test(ua) ? "Android"
    : /iPhone|iPad/i.test(ua) ? "iPhone"
      : /Windows/i.test(ua) ? "Windows"
        : /Mac/i.test(ua) ? "Mac" : "Aparato";
  const instalada = window.matchMedia?.("(display-mode: standalone)").matches;
  return `${sistema}${instalada ? " (app instalada)" : " (navegador)"}`;
}

const GRUPOS = [
  {
    id: "clases",
    icono: "📅",
    titulo: "Mis clases",
    ejemplo: "«Mañana tienes clase a las 6» · «Tu profesor movió tu clase»",
  },
  {
    id: "plan",
    icono: "⚡",
    titulo: "Mi plan y mi energía",
    ejemplo: "«Tu prueba termina mañana» · «Te quedaste sin Energía Spacy»",
  },
];

export default function AvisosDelAlumno() {
  const [estado, setEstado] = useState("mirando");
  const [clave, setClave] = useState("");
  const [prefs, setPrefs] = useState({ clases: true, plan: true });
  const [aparatos, setAparatos] = useState(0);
  const [aviso, setAviso] = useState("");
  const [trabajando, setTrabajando] = useState(false);

  const revisa = useCallback(async () => {
    if (!SOPORTADO) { setEstado("no_soportado"); return; }

    try {
      const [llave, preferencias] = await Promise.all([
        apiClient.get("/api/push/clave-publica"),
        apiClient.get("/api/avisos/preferencias"),
      ]);
      setClave(llave.data?.clave_publica || "");
      setPrefs({
        clases: preferencias.data?.clases !== false,
        plan: preferencias.data?.plan !== false,
      });
      setAparatos(preferencias.data?.aparatos || 0);
      if (!llave.data?.configurado) { setEstado("sin_llaves"); return; }
    } catch {
      setEstado("sin_llaves");
      return;
    }

    //  Se ESPERA al service worker con un reloj al lado. `getRegistration()`
    //  contesta al instante y al arrancar el registro está aún en marcha, así
    //  que decía «no hay» dentro de la app instalada; y `ready` sin reloj no
    //  resuelve nunca si de verdad no hay ninguno, y la pantalla se quedaría
    //  colgada en «Mirando…».
    const registro = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((ok) => setTimeout(() => ok(null), 6000)),
    ]);
    if (!registro) { setEstado("sin_app"); return; }

    if (Notification.permission === "denied") { setEstado("denegado"); return; }
    const suscripcion = await registro.pushManager.getSubscription();
    setEstado(suscripcion ? "activo" : "apagado");
  }, []);

  useEffect(() => { revisa(); }, [revisa]);

  const enciende = async () => {
    setTrabajando(true);
    setAviso("");
    try {
      const permiso = await Notification.requestPermission();
      if (permiso !== "granted") {
        setEstado(permiso === "denied" ? "denegado" : "apagado");
        setAviso("Sin permiso no podemos avisarte. No pasa nada: puedes darlo cuando quieras.");
        return;
      }
      const registro = await navigator.serviceWorker.ready;
      let suscripcion = await registro.pushManager.getSubscription();
      if (!suscripcion) {
        suscripcion = await registro.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: aBytes(clave),
        });
      }
      const bruto = suscripcion.toJSON();
      await apiClient.post("/api/push/suscribir", {
        endpoint: bruto.endpoint,
        keys: { p256dh: bruto.keys.p256dh, auth: bruto.keys.auth },
        aparato: nombreDelAparato(),
      });
      setEstado("activo");
      setAviso("Listo. Te avisaremos aquí.");
      await revisa();
    } catch (e) {
      setAviso(`No se pudo activar: ${e?.message || "error"}`);
    } finally {
      setTrabajando(false);
    }
  };

  const apaga = async () => {
    setTrabajando(true);
    setAviso("");
    try {
      const registro = await navigator.serviceWorker.getRegistration();
      const suscripcion = await registro?.pushManager.getSubscription();
      if (suscripcion) {
        //  Primero el servidor y después el navegador: al revés, si falla la
        //  llamada queda una fila apuntando a un buzón que ya no existe.
        await apiClient.post("/api/push/baja", { endpoint: suscripcion.endpoint })
          .catch(() => {});
        await suscripcion.unsubscribe();
      }
      setEstado("apagado");
      setAviso("Hecho. Ya no te avisaremos en este aparato.");
      await revisa();
    } catch (e) {
      setAviso(`No se pudo apagar: ${e?.message || "error"}`);
    } finally {
      setTrabajando(false);
    }
  };

  const cambiaGrupo = async (id) => {
    const nuevas = { ...prefs, [id]: !prefs[id] };
    setPrefs(nuevas);                       // se pinta ya, sin esperar
    setAviso("");
    try {
      await apiClient.put("/api/avisos/preferencias", nuevas);
    } catch {
      setPrefs(prefs);                      // no se guardó: se deshace
      setAviso("No se pudo guardar. Inténtalo otra vez.");
    }
  };

  const CASOS = {
    mirando: { icono: "…", titulo: "Un momento…", texto: "" },
    no_soportado: {
      icono: "🚫", titulo: "Este navegador no puede avisarte",
      texto: "Prueba desde Chrome en tu teléfono.",
    },
    sin_llaves: {
      icono: "🔔", titulo: "Los avisos no están disponibles ahora",
      texto: "Vuelve a intentarlo más tarde.",
    },
    sin_app: {
      icono: "📲", titulo: "Instala NSL para recibir avisos",
      texto: "Desde el menú de tu navegador, «Añadir a la pantalla de inicio». En iPhone es obligatorio; en Android, recomendable.",
    },
    denegado: {
      icono: "🔕", titulo: "Bloqueaste los avisos en este aparato",
      texto: "Tu navegador ya no va a volver a preguntar. Para recibirlos hay que permitirlos desde los ajustes del sitio: toca el candado junto a la dirección → Notificaciones → Permitir.",
    },
    apagado: {
      icono: "🔔", titulo: "¿Te avisamos en este aparato?",
      texto: "Te recordamos tu clase y te avisamos si tu plan está por terminar. Nada de promociones.",
    },
    activo: {
      icono: "✅", titulo: "Te avisaremos en este aparato",
      texto: "Puedes apagarlo cuando quieras. No afecta a tu cuenta, a tu plan ni a tus clases.",
    },
  };
  const caso = CASOS[estado] || CASOS.mirando;

  return (
    <section className="ava-caja" data-prueba="avisos-alumno" data-estado={estado}>
      <div className="ava-cabecera">
        <span className="ava-icono" aria-hidden="true">{caso.icono}</span>
        <div>
          <h3 className="ava-titulo">{caso.titulo}</h3>
          {caso.texto && <p className="ava-sub">{caso.texto}</p>}
        </div>
      </div>

      <div className="ava-botones">
        {estado === "apagado" && (
          <button type="button" className="ava-boton ava-primario"
                  data-prueba="avisos-alumno-activar"
                  onClick={enciende} disabled={trabajando}>
            {trabajando ? "Activando…" : "Sí, avísame"}
          </button>
        )}
        {estado === "activo" && (
          <button type="button" className="ava-boton ava-secundario"
                  data-prueba="avisos-alumno-apagar"
                  onClick={apaga} disabled={trabajando}>
            Desactivar en este aparato
          </button>
        )}
        {(estado === "denegado" || estado === "sin_app") && (
          <button type="button" className="ava-boton ava-secundario"
                  onClick={revisa} disabled={trabajando}>
            Volver a comprobar
          </button>
        )}
      </div>

      {/* ── LOS GRUPOS ───────────────────────────────────────────────────
          Sólo cuando hay dónde recibirlos. Enseñarlos con cero aparatos
          sería ofrecer elegir entre cosas que no van a llegar. */}
      {estado === "activo" && (
        <div className="ava-grupos" data-prueba="avisos-alumno-grupos">
          <p className="ava-grupos-titulo">¿De qué quieres que te avisemos?</p>
          {GRUPOS.map((g) => (
            <label key={g.id} className="ava-grupo" data-prueba={`avisos-grupo-${g.id}`}>
              <input type="checkbox" checked={Boolean(prefs[g.id])}
                     onChange={() => cambiaGrupo(g.id)} disabled={trabajando} />
              <span className="ava-grupo-texto">
                <span className="ava-grupo-titulo">{g.icono} {g.titulo}</span>
                <span className="ava-grupo-ejemplo">{g.ejemplo}</span>
              </span>
            </label>
          ))}
          {!prefs.clases && !prefs.plan && (
            <p className="ava-sub">
              Con los dos apagados no te llegará nada, aunque el aparato siga
              activado.
            </p>
          )}
          {aparatos > 1 && (
            <p className="ava-nota">
              Esta elección vale para tus {aparatos} aparatos. Lo de arriba es
              solo de éste.
            </p>
          )}
        </div>
      )}

      {aviso && <p className="ava-resultado" data-prueba="avisos-alumno-resultado">{aviso}</p>}

      <p className="ava-nota">
        Solo avisos de tu entrenamiento. Nunca promociones ni ofertas. Puedes
        apagarlos aquí o desde tu navegador, y lo contamos en el{" "}
        <a href="/privacidad" target="_blank" rel="noreferrer">aviso de privacidad</a>.
      </p>
    </section>
  );
}
